/*
 * Licensed to Elasticsearch B.V. under one or more contributor
 * license agreements. See the NOTICE file distributed with
 * this work for additional information regarding copyright
 * ownership. Elasticsearch B.V. licenses this file to you under
 * the Apache License, Version 2.0 (the "License"); you may
 * not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import React, { Component } from 'react';
import PropTypes from "prop-types";
import dateMath from '@elastic/datemath';
import { toSentenceCase } from '../../../../services/string/to_case';
import { htmlIdGenerator } from '../../../../services';
import { EuiFlexGroup, EuiFlexItem } from '../../../flex';
import { EuiForm, EuiFormRow, EuiSelect, EuiFieldNumber, EuiFieldText, EuiSwitch, EuiFormLabel } from '../../../form';
import { EuiSpacer } from '../../../spacer';
import { timeUnits } from '../time_units';
import { relativeOptions } from '../relative_options';
import { parseRelativeParts, toRelativeStringFromParts } from '../relative_utils';
import { EuiScreenReaderOnly } from '../../../accessibility';
import { EuiI18n } from '../../../i18n';
export class EuiRelativeTab extends Component {
  state = { ...parseRelativeParts(this.props.value),
    sentenceCasedPosition: toSentenceCase(this.props.position)
  };
  generateId = htmlIdGenerator();
  onCountChange = event => {
    const sanitizedValue = parseInt(event.target.value, 10);
    this.setState({
      count: isNaN(sanitizedValue) ? undefined : sanitizedValue
    }, this.handleChange);
  };
  onUnitChange = event => {
    this.setState({
      unit: event.target.value
    }, this.handleChange);
  };
  onRoundChange = event => {
    this.setState({
      round: event.target.checked
    }, this.handleChange);
  };
  handleChange = () => {
    const {
      count,
      round,
      roundUnit,
      unit
    } = this.state;
    const {
      onChange
    } = this.props;

    if (count === undefined || count < 0) {
      return;
    }

    const date = toRelativeStringFromParts({
      count,
      round,
      roundUnit,
      unit
    });
    onChange(date);
  };

  render() {
    const {
      count,
      unit
    } = this.state;
    const relativeDateInputNumberDescriptionId = this.generateId();
    const isInvalid = count === undefined || count < 0;
    const parsedValue = dateMath.parse(this.props.value, {
      roundUp: this.props.roundUp
    });
    const formatedValue = isInvalid || !parsedValue || !parsedValue.isValid() ? '' : parsedValue.locale(this.props.locale || 'en').format(this.props.dateFormat);
    return <EuiForm className="euiDatePopoverContent__padded">
        <EuiFlexGroup gutterSize="s" responsive={false}>
          <EuiFlexItem>
            <EuiI18n tokens={['euiRelativeTab.numberInputError', 'euiRelativeTab.numberInputLabel']} defaults={['Must be >= 0', 'Time span amount']}>
              {([numberInputError, numberInputLabel]) => <EuiFormRow isInvalid={isInvalid} error={isInvalid ? numberInputError : null}>
                  <EuiFieldNumber compressed aria-label={numberInputLabel} aria-describedby={relativeDateInputNumberDescriptionId} data-test-subj={'superDatePickerRelativeDateInputNumber'} value={count} onChange={this.onCountChange} isInvalid={isInvalid} />
                </EuiFormRow>}
            </EuiI18n>
          </EuiFlexItem>
          <EuiFlexItem>
            <EuiI18n token="euiRelativeTab.unitInputLabel" default="Relative time span">
              {unitInputLabel => <EuiSelect compressed aria-label={unitInputLabel} data-test-subj={'superDatePickerRelativeDateInputUnitSelector'} value={unit} options={relativeOptions} onChange={this.onUnitChange} />}
            </EuiI18n>
          </EuiFlexItem>
        </EuiFlexGroup>
        <EuiSpacer size="m" />
        <EuiI18n token="euiRelativeTab.roundingLabel" default="Round to the {unit}" values={{
        unit: timeUnits[unit.substring(0, 1)]
      }}>
          {roundingLabel => <EuiSwitch data-test-subj={'superDatePickerRelativeDateRoundSwitch'} label={roundingLabel} checked={this.state.round} onChange={this.onRoundChange} />}
        </EuiI18n>

        <EuiSpacer size="m" />
        <EuiFieldText compressed value={formatedValue} readOnly prepend={<EuiFormLabel>
              <EuiI18n token="euiRelativeTab.relativeDate" default="{position} date" values={{
          position: this.state.sentenceCasedPosition
        }} />
            </EuiFormLabel>} />
        <EuiScreenReaderOnly>
          <p id={relativeDateInputNumberDescriptionId}>
            <EuiI18n token="euiRelativeTab.fullDescription" default="The unit is changeable. Currently set to {unit}." values={{
            unit
          }} />
          </p>
        </EuiScreenReaderOnly>
      </EuiForm>;
  }

}
EuiRelativeTab.propTypes = {
  dateFormat: PropTypes.string.isRequired,
  locale: PropTypes.any,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  roundUp: PropTypes.bool,
  position: PropTypes.oneOf(["start", "end"]).isRequired
};

try {
  // @ts-ignore
  EuiRelativeTab.displayName = "EuiRelativeTab"; // @ts-ignore

  EuiRelativeTab.__docgenInfo = {
    "description": "",
    "displayName": "EuiRelativeTab",
    "props": {
      "dateFormat": {
        "defaultValue": null,
        "description": "",
        "name": "dateFormat",
        "required": true,
        "type": {
          "name": "string"
        }
      },
      "locale": {
        "defaultValue": null,
        "description": "",
        "name": "locale",
        "required": false,
        "type": {
          "name": "string | boolean | Moment | Duration | string[] | undefined"
        }
      },
      "value": {
        "defaultValue": null,
        "description": "",
        "name": "value",
        "required": true,
        "type": {
          "name": "string"
        }
      },
      "onChange": {
        "defaultValue": null,
        "description": "",
        "name": "onChange",
        "required": true,
        "type": {
          "name": "(date: string | null, event?: SyntheticEvent<any, Event> | undefined) => void"
        }
      },
      "roundUp": {
        "defaultValue": null,
        "description": "",
        "name": "roundUp",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "position": {
        "defaultValue": null,
        "description": "",
        "name": "position",
        "required": true,
        "type": {
          "name": "enum",
          "value": [{
            "value": "\"end\""
          }, {
            "value": "\"start\""
          }]
        }
      }
    }
  }; // @ts-ignore

  if (typeof STORYBOOK_REACT_CLASSES !== "undefined") // @ts-ignore
    STORYBOOK_REACT_CLASSES["src/components/date_picker/super_date_picker/date_popover/relative_tab.tsx#EuiRelativeTab"] = {
      docgenInfo: EuiRelativeTab.__docgenInfo,
      name: "EuiRelativeTab",
      path: "src/components/date_picker/super_date_picker/date_popover/relative_tab.tsx#EuiRelativeTab"
    };
} catch (__react_docgen_typescript_loader_error) {}